/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { StyleSheet, css } from 'aphrodite';

import { getCookieValue } from 'id-utils/cookies';
import { getConfig } from 'id-utils/config';
import { getClientAppBaseUrl } from 'id-api/clientApp';
import { Route, Switch, useLocation } from 'react-router-dom';
import { ActionButton, AlertBar, Header, BrandColors } from '@blend/enterprise-components';

import { AppContainer } from '../AppContainer';
import { ClientAppNotFoundPage } from './ClientAppNotFoundPage';

export namespace AuthenticatedApp {
  export interface Props {}
}

// If an upstream redirect landed us here with ?error=<code>, surface fixed
// client-side copy for the code and give the user an explicit choice — don't
// auto-forward into the currently authenticated session. The raw query param
// is never rendered: unknown codes fall through to the normal authenticated
// view, so an attacker-crafted ?error= can't spoof text in the shell.
const ERROR_COPY: Record<string, string> = {
  // /auth/activate session-conflict guard (PFND-7082)
  session_conflict: 'Please sign out of the current account to activate a new one.',
};

const readErrorCode = (search: string): string => {
  const params = new URLSearchParams(search);
  return params.get('error') || '';
};

const styles = StyleSheet.create({
  errorCardWrapper: {
    backgroundColor: BrandColors.WHITE,
    borderRadius: 4,
    padding: `24px 40px`,
    width: 480,
  },
  headerContainer: {
    marginBottom: 16,
  },
  spacer: {
    marginBottom: 12,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 24,
  },
});

export const AuthenticatedApp: React.FC<AuthenticatedApp.Props> = () => {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [baseUrl, setClientAppBaseUrl] = useState<string>('');

  // Capture the error code once on mount so subsequent re-renders don't
  // re-read a stale URL. Use useLocation (not window.location) so tests can
  // drive the URL via MemoryRouter initialEntries (matches BrowserRouter in
  // production — both expose location.search).
  const location = useLocation();
  const [errorCode] = useState<string>(() => readErrorCode(location.search));
  const errorMessage = ERROR_COPY[errorCode] || '';

  const loadBaseUrl = async () => {
    try {
      const config = getConfig();
      const clientAppIdFromCookie = getCookieValue(config.clientAppIdCookieName);
      if (!!clientAppIdFromCookie) {
        const res = await getClientAppBaseUrl(clientAppIdFromCookie);
        setClientAppBaseUrl(res.data.baseUrl);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadBaseUrl();
  }, []);

  if (isLoading) return null;

  if (errorMessage) {
    // Single acknowledgement action. We deliberately don't offer a Sign out
    // button here — logging the user out of a client app from Identity is a
    // cross-domain dance that doesn't belong on an error page. Show the
    // message and let the user handle logout wherever they originated from.
    // OK returns them to the client app's baseUrl (or Identity root if we
    // couldn't resolve it).
    //
    // Rendered inline rather than via FormCard: FormCard has its own
    // useQuery() hook whose precedence puts the raw URL's ?error= value above
    // the errorMessage prop — routing this through FormCard would have let
    // a crafted ?error=<attacker text> render verbatim.
    const dismissHandler = () => {
      window.location.assign(baseUrl || '/');
    };
    return (
      <AppContainer>
        <div id="it-AuthenticatedError-FormCard" className={css(styles.errorCardWrapper)}>
          <div className={css(styles.headerContainer)}>
            <div className={css(styles.spacer)}>
              <Header colorName="NIGHT_90" size={2}>
                Action required
              </Header>
            </div>
          </div>
          <div className={css(styles.spacer)}>
            <AlertBar id="it-AuthenticatedError-error" message={errorMessage} theme="error" />
          </div>
          <div className={css(styles.footer)}>
            <ActionButton id="it-AuthenticatedError-submit" label="OK" onClick={dismissHandler} />
          </div>
        </div>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Switch>
        <Route exact path="/client/client-app-not-found">
          <ClientAppNotFoundPage />
        </Route>
        <Route
          path="*"
          component={() => {
            if (!!baseUrl) {
              // this redirect is sort of like a return
              window.location.assign(baseUrl);
            }
            return <h1>You are logged in. Please navigate directly to the application you'd like to access!</h1>;
          }}
        />
      </Switch>
    </AppContainer>
  );
};
